import { validator } from "@/utils/validator";
import { useMemo } from "react";
import { INVALID_TEL_NUMBER } from "@/constants/invalids";
import {
  MAX_CHARACTER_EMAIL_VALIDATION,
  MAX_CHARACTER_INPUT_VALIDATION,
  MAX_COMPANY_INPUT_VALIDATION,
  MAX_COMPANY_PHONE_VALIDATION,
  MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION,
  MAX_TEXT_INPUT_VALIDATION,
} from "@/constants/common";

type TUseGetCompanySchemaOptions = {
  isCompanyUser?: boolean;
};

export const useGetCompanySchema = ({ isCompanyUser = false }: TUseGetCompanySchemaOptions = {}) => {
  const companySchema = useMemo(() => {
    const startDateSchema = validator
      .mixed()
      .isSelection()
      .isDayjs()
      .label("利用開始日")
      .isBeforeDateOfField("endDate", { startLabel: "利用開始日", endLabel: "利用終了日" });

    return validator.object().shape({
      managementNumber: validator.string().trim().emptyToNull().label("管理番号").max(MAX_CHARACTER_INPUT_VALIDATION).nullable(),
      companyName: validator.string().emptyToNull().autoTransformToFullWidth().trim().label("会社・法人名").required().max(MAX_COMPANY_INPUT_VALIDATION),
      companyKanaName: validator
        .string()
        .trim()
        .katakanaToHiragana()
        .hiraganaOnly()
        .emptyToNull()
        .label("会社・法人名（ふりがな）")
        .nullable()
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .notRequired(),
      companySpecificIndustryId1: validator.number().isSelection().emptyToNull().label("特定産業分野１").required(),
      companySpecificIndustryId2: validator.number().isSelection().emptyToNull().label("特定産業分野２").notRequired(),
      companySpecificIndustryId3: validator.number().isSelection().emptyToNull().label("特定産業分野３").notRequired(),
      companySpecificBusinessClassificationIds1: validator.array().of(validator.string().required()).default([]).label("業務区分１").notRequired(),
      companySpecificBusinessClassificationIds2: validator.array().of(validator.string().required()).default([]).label("業務区分２").notRequired(),
      companySpecificBusinessClassificationIds3: validator.array().of(validator.string().required()).default([]).label("業務区分３").notRequired(),
      hiddenIndustryIds: validator.array().of(validator.number().required()).default([]).notRequired(),
      startDate: isCompanyUser
        ? startDateSchema.notRequired().nullable()
        : startDateSchema.required(),
      endDate: validator
        .mixed()
        .isSelection()
        .isDayjs()
        .isAfterDateOfField("startDate", { startLabel: "利用開始日", endLabel: "利用終了日" })
        .label("利用終了日")
        .notRequired()
        .nullable(),

      contactPersonName: validator
        .string()
        .trim()
        .emptyToNull()
        .label("名前")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .nullable(),
      contactPersonJobTitle: validator
        .string()
        .trim()
        .emptyToNull()
        .label("役職")
        .max(MAX_REPRESENTATIVE_NAME_INPUT_VALIDATION)
        .nullable(),
      contactPersonEmail: validator
        .string()
        .trim()
        .emptyToNull()
        .test("is-email", "正しいメールアドレス形式で入力してください。", (value) => {
          if (!value) return true;
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        })
        .label("メールアドレス")
        .max(MAX_CHARACTER_EMAIL_VALIDATION)
        .nullable(),
      // visa 連携の電話番号 (中継事業者番号/国際番号含む) を編集できるよう、半角数字とハイフンのみ・最大20文字。
      // isTelNumber は日本国内形式のみ許可するため使わず、blur 時の自動整形 (10/11桁) のみ isPhone meta で有効化する。
      contactPersonPhone: validator
        .string()
        .trim()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .meta({ isPhone: true })
        .matches(/^[0-9-]+$/, { message: INVALID_TEL_NUMBER, excludeEmptyString: true })
        .label("電話番号")
        .max(MAX_COMPANY_PHONE_VALIDATION)
        .nullable(),

      note: validator.string().trim().emptyToNull().label("メモ").notRequired().max(MAX_TEXT_INPUT_VALIDATION),
    });
  }, [isCompanyUser]);

  return { companySchema };
};
